import { Box } from '@mui/system';
import { deepmerge } from '@mui/utils';
import { ApexOptions } from 'apexcharts';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import SkeletonGroupedBarChartCard from '../../../../components/Metrics/SkeletonColumnChartCard';
import theme from '../../../../theme';
import { formatNumber } from '../../HatUtils';
import { useHatDbContext } from '../../context/HatDbContext';
import { hatMetrics, PRIORTY_CURRENT } from '../../HatMetrics';
import { useHatQuery } from '../../HatPriorityQueryQueue';
import { HatDbChartCard } from './HatDbChartCard';

export function apexLineChartOptionsFactory(options: {}): ApexOptions {
    const labelFontStyle = {
        fontFamily: theme.typography.fontFamily,
        fontSize: `${theme.typography.body2.fontSize}`,
        fontWeight: theme.typography.body2.fontWeight,
    };

    return {
        chart: {
            type: 'line',
            height: '100%',
            redrawOnWindowResize: true,
            redrawOnParentResize: true,
            toolbar: {
                show: false,
            },
        },
        stroke: {
            // No stroke for the column series and a thicker stroke for the line series
            width: [0, 4],
        },
        plotOptions: {
            bar: {
                columnWidth: '50%',
                borderRadius: 4,
            },
        },
        xaxis: {
            type: 'datetime',
            labels: {
                rotate: -45,
                style: {
                    ...labelFontStyle,
                },
                formatter: function (val) {
                    return new Date(val).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                    });
                },
            },
        },
        colors: [theme.palette.grey[300], theme.palette.primary.main],
        yaxis: [
            {
                title: {
                    style: {
                        ...labelFontStyle,
                    },
                    text: 'Total Impressions',
                },
                labels: {
                    style: {
                        ...labelFontStyle,
                    },
                    formatter: (val: number) => {
                        return formatNumber(val);
                    },
                },
            },
            {
                opposite: true,
                title: {
                    style: {
                        ...labelFontStyle,
                    },
                    text: 'Total Visits',
                },
                labels: {
                    style: {
                        ...labelFontStyle,
                    },
                    formatter: (val: number) => {
                        return formatNumber(val);
                    },
                },
            },
        ],
        tooltip: {
            shared: true,
            intersect: false,
            marker: {
                show: false,
            },
        },
        legend: {
            position: 'top',
            markers: {
                size: 14,
            },
        },
    };
}

const defaultChartOptions = apexLineChartOptionsFactory({});

export type HatMetricPerformanceData = {
    visits: any;
    impressions: any;
};

export function HatDbVisualPerformanceLineChartCard() {
    const hatDbContext = useHatDbContext();
    //const hatDbDataContext = useHatDbDataContext();

    // Get the data from the queries.
    const currentCriteria = hatDbContext.getCurrentRangeCriteria();
    const visitsQuery = useHatQuery(
        {
            ...hatMetrics.trendVisits,
            ...currentCriteria,
            priority: PRIORTY_CURRENT,
        },
        !!currentCriteria
    );

    const impressionQuery = useHatQuery(
        {
            ...hatMetrics.trendImpressions,
            ...currentCriteria,
            priority: PRIORTY_CURRENT,
        },
        !!currentCriteria
    );

    const currentData = useMemo(() => {
        const currentData: HatMetricPerformanceData = {
            visits: null,
            impressions: null,
        };

        if (!visitsQuery.isPending && !visitsQuery.isError) {
            currentData.visits = visitsQuery.data;
        }

        if (!impressionQuery.isPending && !impressionQuery.isError) {
            currentData.impressions = impressionQuery.data;
        }

        return currentData;
    }, [
        visitsQuery.isPending,
        visitsQuery.isError,
        visitsQuery.data,
        impressionQuery.isPending,
        impressionQuery.isError,
        impressionQuery.data,
    ]);

    const chartOptions: ApexOptions = useMemo(() => {
        const series: ApexAxisChartSeries = [];

        if (currentData.impressions !== null) {
            series.push({
                name: 'Impressions',
                type: 'column',
                data: currentData.impressions.map((v: { x: number; y: number }) => ({ x: new Date(v.x), y: v.y })),
            });
        } else {
            series.push({
                name: 'Impressions (loading...)',
                type: 'line',
                data: [],
            });
        }

        if (currentData.visits !== null) {
            series.push({
                name: 'Visits',
                type: 'line',
                data: currentData.visits.map((v: { x: number; y: number }) => ({ x: new Date(v.x), y: v.y })),
            });
        } else {
            series.push({
                name: 'Visits (loading...)',
                type: 'line',
                data: [],
            });
        }

        return deepmerge(
            defaultChartOptions,
            {
                series,
            },
            {
                clone: true,
            }
        );
    }, [currentData]);

    // At least one result must have come back.
    const isPending = currentData.visits === null && currentData.impressions === null;

    return (
        <HatDbChartCard title="Impressions vs. Visits">
            {isPending && <SkeletonGroupedBarChartCard />}

            {!isPending && (
                <Box
                    sx={{
                        width: '100%',
                        height: '300px',
                    }}
                >
                    <ReactApexChart
                        type="line"
                        options={chartOptions}
                        series={chartOptions.series}
                        width={chartOptions.chart?.width}
                        height={chartOptions.chart?.height}
                    />
                </Box>
            )}
        </HatDbChartCard>
    );
}
